<template>
  <view class="list-item" :style="listStyle">
    <template v-for="(item, index) of data" :key="index">
      <view class="item-row">
        <view class="item-name" :style="nameStyle">{{ item.name }}</view>
        <view class="item-text-container">
          <view class="item-count" :style="countStyle">{{ item.count }} {{ suffix }}</view>
          <view class="item-percent" :style="percentStyle">{{ item.percent }}%</view>
        </view>
      </view>
      <view class="item-progress-container" :style="progressStyles">
        <view class="item-progress" :style="rateClass(item, index)"></view>
      </view>
    </template>
  </view>

  <template v-if="shouldScroll">
    <template v-for="item of Array.from({ length: 3 }, (i, k) => k)" :key="item">
      <view class="list-item" :style="listStyle">
        <template v-for="(item, index) of data" :key="index">
          <view class="item-row">
            <view class="item-name" :style="nameStyle">{{ item.name }}</view>
            <view class="item-text-container">
              <view class="item-count" :style="countStyle">{{ item.count }} {{ suffix }}</view>
              <view class="item-percent" :style="countStyle">{{ item.rate }}%</view>
            </view>
          </view>
          <view class="item-progress-container" :style="progressStyles">
            <view class="item-progress" :style="rateClass(item, index)"></view>
          </view>
        </template>
      </view>
    </template>
  </template>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue"
import { removePx } from "@p-v/utils"

interface ListDataItem {
  name: string
  count: number
  percent: number
  rate?: number
}

interface List3Props {
  style?: { [key: string]: string }
  percentStyle?: { [key: string]: string }
  suffix: string | undefined
  nameStyle?: { [key: string]: string }
  countStyle?: { [key: string]: string }
  rateStyle?: { [key: string]: string }
  rateStyles?: { [key: string]: string }[]
  rateClass?: (item: ListDataItem, index: number) => { [key: string]: string }
  progressStyles?: { [key: string]: string }
  data: ListDataItem[]
  scroll?: boolean
  minHeight?: number
}

const emits = defineEmits(["update:scroll"])
const props = withDefaults(defineProps<List3Props>(), {
  style: () => ({}),
  percentStyle: () => ({}),
  suffix: undefined,
  nameStyle: () => ({}),
  countStyle: () => ({}),
  rateStyle: () => ({}),
  progressStyles: () => ({}),
  rateStyles: () => [],
  rateClass: (item: ListDataItem, index: number) => ({}),
  data: () => [],
  scroll: false,
  minHeight: undefined,
})

const maxHeight = computed(() => {
  let maxHeight = 32
  if (props.nameStyle.height || props.nameStyle.lineHeight) {
    maxHeight = Math.max(maxHeight, Number(removePx(props.nameStyle.lineHeight || props.nameStyle.height)))
  }
  if (props.countStyle.height || props.countStyle.lineHeight) {
    maxHeight = Math.max(maxHeight, Number(removePx(props.countStyle.lineHeight || props.countStyle.height)))
  }
  return maxHeight
})

const progressHeight = computed(() => {
  let progressHeight = 8
  if (props.progressStyles?.height) {
    progressHeight = parseInt(props.progressStyles?.height.replace("px", ""))
  }
  return progressHeight
})

const height = computed(() => {
  return props.data.length * (maxHeight.value + progressHeight.value)
})

const shouldScroll = ref(props.scroll)

const checkScroll = () => {
  if (height.value > 0 && props.minHeight && height.value <= props.minHeight) {
    shouldScroll.value = false
  } else if (height.value > 0 && props.minHeight && height.value > props.minHeight) {
    shouldScroll.value = true
  } else {
    shouldScroll.value = false
  }
  emits("update:scroll", shouldScroll.value)
}

watch(
  () => height.value,
  () => {
    checkScroll()
  },
  { immediate: true },
)

watch(
  () => props.minHeight,
  () => {
    checkScroll()
  },
  { immediate: true },
)

const listStyle = computed(() => {
  const stlyeObj = {
    display: "grid",
    gridTemplateRows: `repeat(${props.data.length}, ${maxHeight.value}px ${progressHeight.value}px)`,
  }
  stlyeObj["height"] = Math.max(height.value, props.minHeight || 0) + "px"
  return { ...stlyeObj }
})
</script>
<style lang="scss" scoped>
.list-item {
  position: relative;
  height: max-content;
  .item-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;
  }
  .item-name {
    position: relative;
    font-family:
      Segoe UI,
      Segoe UI;
    font-weight: 400;
    font-size: 16px;
    color: #ffffff;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    display: flex;
    align-items: center;
    width: 80px;
    flex-grow: 1;
  }
  .item-text-container {
    position: relative;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
  .item-count {
    font-family:
      Segoe UI,
      Segoe UI;
    font-weight: 400;
    font-size: 16px;
    color: #ffffff;
    text-shadow: 0 0 3px BLACK;
    position: relative;
    text-align: right;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-right: 10px;
  }
  .item-percent {
    font-size: 16px;
  }
  .item-progress-container {
    position: relative;
    width: 100%;
    height: 8px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px 4px 4px 4px;
    margin: auto 0;
    .item-progress {
      display: block;
      height: 8px;
      border-radius: 4px 4px 4px 4px;
      background: linear-gradient(90deg, rgba(56, 112, 212, 0.2) 0%, #2bb7f7 100%);
    }
  }
  .item-suffix {
    font-family:
      Segoe UI,
      Segoe UI;
    font-size: 14px;
    color: #ffffff80;
    text-align: right;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
}
</style>
